// Multi-territory Assign Entity API service
import {
  MT_ASSIGN_ENTITY_CREATE_API,
  MT_ASSIGN_ENTITY_DELETE_API,
  MT_ASSIGN_ENTITY_DOWNLOAD_API,
  MT_ASSIGN_ENTITY_LIST_API,
  MT_COUNTRY_DROPDOWN_API,
  MT_ENTITY_DROPDOWN_BY_COUNTRY_API,
  MT_TAN_DROPDOWN_BY_ENTITY_API,
  MT_USER_LIST_API,
} from '@/multi-territory/mt-services/mt-api-endpoints'
import {
  MTApiResponse,
  mtBuildRequestHeader,
  mtHandleErrorResponse,
  mtHandleRequestFailure,
  mtParseJsonResponse,
  mtParseMessageResponse,
  mtUrlBuilder,
} from '@/multi-territory/mt-services/mt-api-utils'
import type {
  MTAssignEntityCreateRequest,
  MTAssignEntityListRequest,
  MTAssignEntityListResponse,
  MTCountryOption,
  MTEntityDropdownItem,
  MTTanDropdownItem,
  MTUserItem,
  MTUserListRequest,
  MTUserListResponse,
} from '@/multi-territory/mt-types/mt-admin'

// Re-export types for consumer convenience
export type {
  MTAssignEntityCreateRequest,
  MTAssignEntityListRequest,
  MTAssignEntityListResponse,
  MTEntityDropdownItem,
  MTTanDropdownItem,
  MTUserItem,
  MTUserListResponse
}

/**
 * Fetch country dropdown list (generic).
 * GET /wht-admin/api/admin/api/v1/countrymaster/dropdown
 */
export const getAssignEntityCountryDropdown = async (): Promise<
  MTApiResponse<MTCountryOption[]>
> => {
  const url = mtUrlBuilder(MT_COUNTRY_DROPDOWN_API)
  const requestInit = mtBuildRequestHeader('GET')
  try {
    const response = await fetch(url, requestInit)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseJsonResponse<MTCountryOption[]>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Fetch user list with pagination.
 * POST /wht-admin/api/v1/user/list
 */
export const getUserList = async (
  payload: MTUserListRequest
): Promise<MTApiResponse<MTUserListResponse>> => {
  const url = mtUrlBuilder(MT_USER_LIST_API)
  const requestInit = mtBuildRequestHeader('POST', payload)
  try {
    const response = await fetch(url, requestInit)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseJsonResponse<MTUserListResponse>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Fetch entity dropdown filtered by country UUIDs.
 * POST /wht-admin/api/v1/entity-master/dropdown/country-uuids
 */
export const getEntityDropdownByCountry = async (
  countryUuids: string[]
): Promise<MTApiResponse<MTEntityDropdownItem[]>> => {
  const url = mtUrlBuilder(MT_ENTITY_DROPDOWN_BY_COUNTRY_API)
  const requestInit = mtBuildRequestHeader('POST', countryUuids)
  try {
    const response = await fetch(url, requestInit)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseJsonResponse<MTEntityDropdownItem[]>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Fetch TAN dropdown filtered by entity UUIDs.
 * POST /wht-admin/api/v1/tan-master/dropdown/entities
 */
export const getTanDropdownByEntities = async (
  entityUuids: string[]
): Promise<MTApiResponse<MTTanDropdownItem[]>> => {
  const url = mtUrlBuilder(MT_TAN_DROPDOWN_BY_ENTITY_API)
  const requestInit = mtBuildRequestHeader('POST', entityUuids)
  try {
    const response = await fetch(url, requestInit)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseJsonResponse<MTTanDropdownItem[]>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Fetch assigned entity listing with pagination.
 * POST /wht-mt-admin/api/v1/assign-entity/list
 */
export const getAssignEntityList = async (
  payload: MTAssignEntityListRequest
): Promise<MTApiResponse<MTAssignEntityListResponse>> => {
  const url = mtUrlBuilder(MT_ASSIGN_ENTITY_LIST_API)
  const requestInit = mtBuildRequestHeader('POST', payload)
  try {
    const response = await fetch(url, requestInit)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseJsonResponse<MTAssignEntityListResponse>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Create assign entity records.
 * POST /wht-admin/api/v1/user/assign-entity
 */
export const createAssignEntity = async (
  payload: MTAssignEntityCreateRequest
): Promise<MTApiResponse<string>> => {
  const url = mtUrlBuilder(MT_ASSIGN_ENTITY_CREATE_API)
  const requestInit = mtBuildRequestHeader('POST', payload)
  try {
    const response = await fetch(url, requestInit)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseMessageResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Delete assigned entity records by UUIDs.
 * POST /wht-mt-admin/api/v1/assign-entity/delete
 */
export const deleteAssignEntity = async (
  uuids: string[]
): Promise<MTApiResponse<string>> => {
  const url = mtUrlBuilder(MT_ASSIGN_ENTITY_DELETE_API)
  const requestInit = mtBuildRequestHeader('POST', uuids)
  try {
    const response = await fetch(url, requestInit)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseMessageResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Download assign entity data as Excel.
 * POST /wht-mt-admin/api/v1/assign-entity/download
 */
export const downloadAssignEntity = async (payload: {
  pageFilter: null
  searchCOList: any[]
}): Promise<MTApiResponse<Blob>> => {
  const url = mtUrlBuilder(MT_ASSIGN_ENTITY_DOWNLOAD_API)
  const requestInit = mtBuildRequestHeader('POST', payload)
  try {
    const response = await fetch(url, requestInit)
    if (!response.ok) return await mtHandleRequestFailure(response)
    const blob = await response.blob()
    return { success: true, data: blob }
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}
